import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import { useIsAuthenticated } from '../../state_management_and_stores/auth.store';

// --- Query Key Factory ---
export const jackpotOpsKeys = {
  all: ['jackpot-ops'] as const,
  lists: () => [...jackpotOpsKeys.all, 'list'] as const,
  list: (filters?: JackpotsListFilters) =>
    filters
      ? ([...jackpotOpsKeys.all, 'list', filters] as const)
      : ([...jackpotOpsKeys.all, 'list'] as const),
  detail: (jackpotId: string) =>
    [...jackpotOpsKeys.all, 'detail', jackpotId] as const,
  rulesets: () => [...jackpotOpsKeys.all, 'rulesets'] as const,
  eventStatuses: () => [...jackpotOpsKeys.all, 'event-statuses'] as const,
  userOrganizations: () =>
    [...jackpotOpsKeys.all, 'user-organizations'] as const,
  eventRulesets: (eventId: string) =>
    [...jackpotOpsKeys.all, 'event-rulesets', eventId] as const,
};

// --- Types ---
interface Jackpot {
  id: string;
  name: string;
  rodeo_or_jackpot: string;
  organization_id: string;
  is_published: boolean;
  events?: JackpotEvent[];
  [key: string]: unknown;
}

interface JackpotEvent {
  id: string;
  event_name: string;
  [key: string]: unknown;
}

interface CreateDraftData {
  name: string;
  organization_id: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  rodeo_or_jackpot?: string;
  events?: Array<{
    event_name: string;
    event_type?: string;
    entry_fee?: number;
    rounds?: Array<{
      round_number: number;
      round_type?: string;
    }>;
  }>;
  [key: string]: unknown;
}

interface UpdateDraftData {
  name?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  events?: Array<{
    id?: string;
    event_name: string;
    event_type?: string;
    entry_fee?: number;
    rounds?: Array<{
      id?: string;
      round_number: number;
      round_type?: string;
    }>;
  }>;
  [key: string]: unknown;
}

interface JackpotsListFilters {
  page?: number;
  pageSize?: number;
  organization_id?: string;
  is_published?: boolean;
  search?: string;
  /**
   * Discriminator for the rodeos table. Backend defaults to 'jackpot' when
   * omitted, so existing callers stay on jackpots; pass 'rodeo' to fetch the
   * rodeo column on the dashboard's two-column upcoming layout.
   */
  type?: 'jackpot' | 'rodeo';
}

interface Ruleset {
  id: string;
  name: string;
  description?: string;
  [key: string]: unknown;
}

interface EventStatus {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface Organization {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface EventRuleset {
  id: string;
  event_id: string;
  ruleset_id: string;
  [key: string]: unknown;
}

interface AddEventRulesetData {
  ruleset_id: string;
}

interface UpdateJackpotData {
  name?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  [key: string]: unknown;
}

// --- Query Hooks ---
export function useJackpotsList(filters?: JackpotsListFilters) {
  const qs = new URLSearchParams();
  if (filters?.page) qs.set('page', String(filters.page));
  if (filters?.pageSize) qs.set('pageSize', String(filters.pageSize));
  if (filters?.organization_id)
    qs.set('organization_id', filters.organization_id);
  if (filters?.is_published !== undefined)
    qs.set('is_published', String(filters.is_published));
  if (filters?.search) qs.set('search', filters.search);
  if (filters?.type) qs.set('type', filters.type);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: jackpotOpsKeys.list(filters),
    queryFn: () =>
      httpClient.get<{ data: Jackpot[]; total: number }>(
        `/jackpot-ops/list${query}`,
      ),
  });
}

export function useJackpotDetail(jackpotId: string) {
  return useQuery({
    queryKey: jackpotOpsKeys.detail(jackpotId),
    queryFn: () => httpClient.get<Jackpot>(`/jackpot-ops/${jackpotId}`),
    enabled: !!jackpotId,
  });
}

export function useRulesets() {
  return useQuery({
    queryKey: jackpotOpsKeys.rulesets(),
    queryFn: () => httpClient.get<Ruleset[]>('/jackpot-ops/meta/rulesets'),
  });
}

export function useEventStatuses() {
  return useQuery({
    queryKey: jackpotOpsKeys.eventStatuses(),
    queryFn: () =>
      httpClient.get<EventStatus[]>('/jackpot-ops/meta/event-statuses'),
  });
}

export function useUserOrganizations() {
  // "Your organizations" is auth-scoped. Skip for visitors so public-browse
  // pages don't fire a 401 on /jackpot-ops/meta/organizations.
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: jackpotOpsKeys.userOrganizations(),
    enabled: isAuthenticated,
    queryFn: () =>
      httpClient.get<Organization[]>('/jackpot-ops/meta/organizations'),
  });
}

export function useEventRulesets(eventId: string) {
  return useQuery({
    queryKey: jackpotOpsKeys.eventRulesets(eventId),
    queryFn: () =>
      httpClient.get<EventRuleset[]>(
        `/jackpot-ops/events/${eventId}/rulesets`,
      ),
    enabled: !!eventId,
  });
}

// --- Mutation Hooks ---
export function useCreateJackpotDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDraftData) =>
      httpClient.post<Jackpot>('/jackpot-ops/draft', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
    },
  });
}

export function useUpdateJackpotDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      jackpotId,
      ...data
    }: UpdateDraftData & { jackpotId: string }) =>
      httpClient.put<Jackpot>(`/jackpot-ops/draft/${jackpotId}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: jackpotOpsKeys.detail(variables.jackpotId),
      });
      qc.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
    },
  });
}

export function useCreateRuleset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      httpClient.post<Ruleset>('/jackpot-ops/meta/rulesets', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jackpotOpsKeys.rulesets() });
    },
  });
}

export function useAddEventRuleset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: AddEventRulesetData & { eventId: string }) =>
      httpClient.post<EventRuleset>(
        `/jackpot-ops/events/${eventId}/rulesets`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: jackpotOpsKeys.eventRulesets(variables.eventId),
      });
    },
  });
}

export function useRemoveEventRuleset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      rulesetId,
    }: {
      eventId: string;
      rulesetId: string;
    }) =>
      httpClient.delete(
        `/jackpot-ops/events/${eventId}/rulesets/${rulesetId}`,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: jackpotOpsKeys.eventRulesets(variables.eventId),
      });
    },
  });
}

export function useUpdateJackpot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      jackpotId,
      ...data
    }: UpdateJackpotData & { jackpotId: string }) =>
      httpClient.put<Jackpot>(`/jackpot-ops/${jackpotId}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: jackpotOpsKeys.detail(variables.jackpotId),
      });
      qc.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
    },
  });
}

export function useToggleJackpotPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jackpotId: string) =>
      httpClient.request(`/jackpot-ops/${jackpotId}/toggle-publish`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jackpotOpsKeys.all });
    },
  });
}

export function useDeleteJackpot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jackpotId: string) =>
      httpClient.delete(`/jackpot-ops/${jackpotId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
    },
  });
}
